import { For, Show, type JSX } from 'solid-js'
import type { NavGroupScope, NavItemScope } from 'mormaii-web-presentation/restricted'
import { Icon } from '../icons'
import { Brand, LicensedSeal } from '../icons'
import Css from './shell.module.scss'

/**
 * A casca do painel: barra lateral e cabecalho.
 *
 * ## Por que nao e a barra do painel antigo
 *
 * La a barra mede 48px recolhida e ABRE COM O MOUSE, com um botao que a congela
 * aberta. Sao tres estados (`open`, `frozeOpen`, e o inverso disso no celular) para
 * economizar 200px de largura, e o resultado e um menu que se mexe sozinho quando o
 * ponteiro passa raspando. Aqui a barra e FIXA e sempre legivel no desktop, e uma
 * gaveta no celular. Um estado em vez de tres.
 *
 * ## O escuro
 *
 * A barra e escura pela mesma razao que o painel da pagina de conta e: e o que
 * separa a moldura do conteudo sem precisar de borda, e e o contraste que a
 * referencia visual do cliente usa. O conteudo fica claro, porque e onde se le
 * tabela.
 */

type SidebarProps = {
    groups: NavGroupScope[]
    /** Se a gaveta esta aberta - so vale abaixo de `lg`, onde a barra e gaveta. */
    open: boolean
    onClose: () => void
}

export function Sidebar(props: SidebarProps): JSX.Element {
    return (
        <>
            {/*
                O veu da gaveta. So existe no celular e so quando aberta - no
                desktop a barra e parte do arranjo e nao cobre nada.
            */}
            <Show when={props.open}>
                <button type="button" aria-label="Fechar menu" onClick={props.onClose} class={Css.backdrop} />
            </Show>

            <aside class={Css.sidebar} classList={{ [Css.closed]: !props.open }}>
                <div class={Css.brand}>
                    <Brand class={Css.brandIcon} />
                    <span class={Css.brandName}>
                        Mormaii <span class={Css.brandAccent}>Mais Saúde</span>
                    </span>
                </div>

                <nav class={Css.nav}>
                    <For each={props.groups}>
                        {(group) => (
                            <div class={Css.group}>
                                <p class={Css.groupTitle}>{group.name}</p>
                                <For each={group.items}>
                                    {(item) => <NavItem scope={item} onNavigate={props.onClose} />}
                                </For>
                            </div>
                        )}
                    </For>
                </nav>

                <div class={Css.version}>
                    <LicensedSeal class={Css.licensedSeal} />
                    <p>
                        © {new Date().getFullYear()} Mormaii Mais Saúde
                        <br />
                        Painel v{import.meta.env.VITE_APP_VERSION}
                    </p>
                </div>
            </aside>
        </>
    )
}

/**
 * Um item do menu.
 *
 * Continua um `<a>` de verdade quando tem endereco - abrir uma secao em outra aba e
 * uma coisa que se faz -, mas o clique e interceptado: quem navega e o presenter, e
 * deixar o navegador seguir o `href` recarregaria a aplicacao inteira.
 */
function NavItem(props: { scope: NavItemScope; onNavigate: () => void }): JSX.Element {
    const select = (event: Event) => {
        event.preventDefault()
        props.onNavigate()
        void props.scope.onSelect()
    }

    const body = (
        <span class={Css.item} classList={{ [Css.current]: props.scope.current }}>
            <Icon name={props.scope.icon} size={18} class={Css.itemIcon} />
            <span class={Css.itemLabel}>{props.scope.label}</span>
            <Show when={props.scope.notification}>{(count) => <span class={Css.itemCount}>{count()}</span>}</Show>
        </span>
    )

    return (
        <Show
            when={props.scope.href}
            fallback={
                <button type="button" onClick={select} class={Css.wrapper}>
                    {body}
                </button>
            }
        >
            <a href={props.scope.href} onClick={select} class={Css.wrapper}>
                {body}
            </a>
        </Show>
    )
}
